<template>
  <transition name="pc-goods-detail" appear>
    <div class="pc-goods-detail" @click="close">
      <img
        class="pc-goods-detail__bg"
        src="@/assets/image/goods/bg.webp"
        alt="background image"
        decoding="async"
      />
      <div
        class="pc-goods-detail__marquee pc-goods-detail__marquee--top pc-goods-detail__marquee--skin-goods"
        @click.stop
      >
        <div class="pc-goods-detail__marquee-frame">
          <div class="pc-goods-detail__marquee-strip">
            <div class="pc-goods-detail__marquee-track">
              <div
                v-for="dup in 2"
                :key="'pc-goods-detail-m-' + dup"
                class="pc-goods-detail__marquee-chunk"
                :aria-hidden="dup === 2 ? 'true' : undefined"
              >
                <img
                  v-for="item in 10"
                  :key="`pc-goods-detail-m-${dup}-${item}`"
                  class="pc-goods-detail__marquee-img"
                  src="@/assets/image/index/banner_footer_title.webp"
                  width="314"
                  height="30"
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pc-goods-detail__backdrop" aria-hidden="true">
        <img
          class="pc-goods-detail__backdrop-bg pc-goods__hero-bg"
          src="@/assets/image/goods/page_bg.webp"
          alt="Goods detail page background"
          width="1920"
          height="1036"
          decoding="async"
          loading="lazy"
        />
      </div>

      <div
        ref="backdropContent"
        class="pc-goods-detail__backdrop-content"
        @click.stop
        :aria-labelledby="
          currentGoods && normalizeGoodsTitleLines(currentGoods).length
            ? 'pc-goods-detail-heading-' + activeIndex
            : undefined
        "
      >
        <button
          type="button"
          class="pc-goods-detail__btn pc-goods-detail__btn--prev"
          :disabled="!canPrev"
          :aria-label="goodsDetailCopy.ariaPrevProduct"
          @click.stop="onPrev"
        >
          <img
            src="@/assets/image/detail/last.webp"
            alt=""
            width="40"
            height="40"
            decoding="async"
            role="presentation"
          />
        </button>
        <button
          type="button"
          class="pc-goods-detail__btn pc-goods-detail__btn--next"
          :disabled="!canNext"
          :aria-label="goodsDetailCopy.ariaNextProduct"
          @click.stop="onNext"
        >
          <img
            src="@/assets/image/detail/next.webp"
            alt=""
            width="40"
            height="40"
            decoding="async"
            role="presentation"
          />
        </button>

        <Swiper
          v-if="goodsList.length"
          ref="detailSwiper"
          class="pc-goods-detail__swiper"
          :options="swiperOptions"
          @slideChangeTransitionEnd="onSlideChangeTransitionEnd"
        >
          <SwiperSlide
            v-for="(slideItem, idx) in goodsList"
            :key="'goods-detail-slide-' + idx"
            class="pc-goods-detail__slide"
          >
            <div class="pc-goods-detail__panel">
              <button
                type="button"
                class="pc-goods-detail__close"
                :aria-label="goodsDetailCopy.ariaCloseProductDetails"
                @click.stop="close"
              >
                <img
                  src="@/assets/image/detail/close.webp"
                  alt=""
                  width="40"
                  height="40"
                  decoding="async"
                  role="presentation"
                />
              </button>

              <div class="pc-goods-detail__gallery-stack">
                <div
                  class="pc-goods-detail__gallery"
                  :class="{
                    'pc-goods-detail__gallery--multi':
                      normalizeGoodsImages(slideItem).length > 1,
                  }"
                >
                  <button
                    v-if="normalizeGoodsImages(slideItem).length > 1"
                    type="button"
                    class="pc-goods-detail__gallery-nav pc-goods-detail__gallery-nav--prev"
                    :disabled="!innerImgCanPrev(idx)"
                    :aria-label="goodsDetailCopy.ariaPrevProductImage"
                    @click.stop="onInnerImgPrev(idx)"
                  >
                    <img
                      src="@/assets/image/detail/img_last.webp"
                      alt=""
                      width="40"
                      height="40"
                      decoding="async"
                      role="presentation"
                    />
                  </button>
                  <button
                    v-if="normalizeGoodsImages(slideItem).length > 1"
                    type="button"
                    class="pc-goods-detail__gallery-nav pc-goods-detail__gallery-nav--next"
                    :disabled="!innerImgCanNext(idx)"
                    :aria-label="goodsDetailCopy.ariaNextProductImage"
                    @click.stop="onInnerImgNext(idx)"
                  >
                    <img
                      src="@/assets/image/detail/img_next.webp"
                      alt=""
                      width="40"
                      height="40"
                      decoding="async"
                      role="presentation"
                    />
                  </button>
                  <div class="pc-goods-detail__gallery-viewport">
                    <div
                      class="pc-goods-detail__gallery-track"
                      :style="innerImgTrackStyle(idx)"
                    >
                      <div
                        v-for="(src, imgI) in normalizeGoodsImages(slideItem)"
                        :key="'inner-img-' + idx + '-' + imgI"
                        class="pc-goods-detail__gallery-slide"
                        :style="innerImgSlideCellStyle(slideItem)"
                      >
                        <div
                          v-if="isGalleryImageLoading(idx, imgI, src)"
                          class="pc-goods-detail__gallery-img-loading"
                          aria-hidden="true"
                        >
                          <span class="pc-goods-detail__gallery-img-spinner" />
                        </div>
                        <img
                          class="pc-goods-detail__gallery-img"
                          :class="{ 'is-loading': isGalleryImageLoading(idx, imgI, src) }"
                          :src="src"
                          :alt="
                            (goodsTitlePlain(slideItem) ||
                              goodsDetailCopy.altProductFallback) +
                            ' image ' +
                            (imgI + 1) +
                            goodsDetailCopy.ariaImageMid +
                            normalizeGoodsImages(slideItem).length
                          "
                          decoding="async"
                          @load="onGalleryImageLoad(idx, imgI, src)"
                          @error="onGalleryImageError(idx, imgI, src)"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="normalizeGoodsImages(slideItem).length > 1"
                    class="pc-goods-detail__gallery-dots"
                    role="tablist"
                    :aria-label="
                      goodsTitlePlain(slideItem)
                        ? goodsTitlePlain(slideItem) +
                          goodsDetailCopy.ariaProductImagesSuffix
                        : goodsDetailCopy.ariaProductImagesFallback
                    "
                  >
                    <button
                      v-for="(src, imgI) in normalizeGoodsImages(slideItem)"
                      :key="'inner-dot-' + idx + '-' + imgI"
                      type="button"
                      role="tab"
                      class="pc-goods-detail__gallery-dot"
                      :class="{
                        'pc-goods-detail__gallery-dot--active':
                          imgI === clampedInnerImgIndex(idx),
                      }"
                      :aria-selected="
                        imgI === clampedInnerImgIndex(idx) ? 'true' : 'false'
                      "
                      :aria-label="
                        goodsDetailCopy.ariaImagePrefix +
                        (imgI + 1) +
                        goodsDetailCopy.ariaImageMid +
                        normalizeGoodsImages(slideItem).length
                      "
                      @click.stop="goInnerImg(idx, imgI)"
                    />
                  </div>
                </div>
              </div>
              <div class="pc-goods-detail__info">
                <img
                  class="pc-goods-detail__info-banner"
                  src="@/assets/image/detail/title.webp"
                  alt=""
                  decoding="async"
                  role="presentation"
                />
                <h2
                  v-if="normalizeGoodsTitleLines(slideItem).length"
                  :id="'pc-goods-detail-heading-' + idx"
                  class="pc-goods-detail__info-title"
                >
                  <span
                    v-for="(line, lineIdx) in normalizeGoodsTitleLines(
                      slideItem
                    )"
                    :key="'pc-goods-detail-title-' + idx + '-' + lineIdx"
                    class="pc-goods-detail__info-title-line"
                  >
                    {{ line }}
                  </span>
                </h2>
                <div class="pc-goods-detail__info-desc-wrapper">
                  <div
                    v-for="(line, infoI) in normalizeGoodsInfoLines(slideItem)"
                    :key="infoI"
                    class="pc-goods-detail__info-desc"
                  >
                    {{ line }}
                  </div>
                </div>

                <div class="pc-goods-detail__info-price">
                  <template v-if="slideItem.priceIsNumeric">
                    <span class="pc-goods-detail__info-price-label">{{
                      goodsDetailCopy.priceLabel
                    }}</span>
                    <span>{{ slideItem.price }}円</span>
                    <span class="pc-goods-detail__info-price-label">{{
                      goodsDetailCopy.taxNote
                    }}</span>
                  </template>
                  <span v-else class="pc-goods-detail__info-price-plain">{{
                    slideItem.price
                  }}</span>
                </div>

                <button type="button" class="pc-goods-detail__info-limit">
                  <span>{{ goodsDetailCopy.purchaseLimitPrefix }}</span>
                  {{ goodsDetailCopy.purchaseLimitBody }}
                </button>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div
        class="pc-goods-detail__marquee pc-goods-detail__marquee--bottom pc-goods-detail__marquee--skin-streaming"
        @click.stop
        aria-hidden="true"
      >
        <div class="pc-goods-detail__marquee-frame">
          <div class="pc-goods-detail__marquee-strip">
            <div class="pc-goods-detail__marquee-track">
              <div
                v-for="dup in 2"
                :key="'pc-goods-detail-streaming-m-' + dup"
                class="pc-goods-detail__marquee-chunk"
                :aria-hidden="dup === 2 ? 'true' : undefined"
              >
                <img
                  v-for="n in 10"
                  :key="`pc-goods-detail-streaming-m-${dup}-${n}`"
                  class="pc-goods-detail__marquee-img"
                  src="@/assets/image/index/about_footer_title.webp"
                  width="314"
                  height="30"
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import goodsContent from "@/views/data/goods-content.json";

export default {
  name: "PcGoodsDetailModal",

  components: {
    Swiper,
    SwiperSlide,
  },

  props: {
    goodsList: {
      type: Array,
      default() {
        return [];
      },
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    canPrev: {
      type: Boolean,
      default: false,
    },
    canNext: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      /** 详情弹层文案（维护 goods-content.json → goodsDetail） */
      goodsDetailCopy: goodsContent.goodsDetail,
      swiperSlideSpeed: 420,
      /** 内层配图横向滑动动画时长（ms），与 reduced motion 同步为 0 */
      innerImgSlideMs: 420,
      /** 每个商品在弹层内的当前配图下标（与外层 Swiper 的 slide 下标对应） */
      innerImgIndexBySlide: {},
      /** 详情弹层图片加载状态：key -> loading | loaded | error */
      galleryImgLoadStatus: {},
      /** 关闭/销毁时 Swiper  teardown 可能误触发 slideChange，避免把父级索引写成 0 */
      suppressSwiperIndexEmit: false,
    };
  },

  computed: {
    currentGoods() {
      const i = this.activeIndex;
      if (i < 0 || i >= this.goodsList.length) return null;
      return this.goodsList[i];
    },
    swiperOptions() {
      const max = Math.max(0, this.goodsList.length - 1);
      const initial = Math.max(
        0,
        Math.min(this.activeIndex < 0 ? 0 : this.activeIndex, max)
      );
      return {
        speed: this.swiperSlideSpeed,
        grabCursor: false,
        simulateTouch: false,
        resistanceRatio: 0.72,
        spaceBetween: 20,
        slidesPerView: 1,
        initialSlide: initial,
        allowTouchMove: false,
      };
    },
  },

  watch: {
    activeIndex(n) {
      if (this.suppressSwiperIndexEmit) return;
      this.$nextTick(() => {
        const inst = this.getSwiper();
        if (!inst) return;
        if (inst.activeIndex !== n) {
          inst.slideTo(n, this.swiperSlideSpeed);
        }
      });
    },
    goodsList: {
      immediate: true,
      handler() {
        this.galleryImgLoadStatus = {};
      },
    },
  },

  mounted() {
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = "hidden";
    }
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      this.swiperSlideSpeed = 0;
      this.innerImgSlideMs = 0;
    }
  },

  beforeDestroy() {
    this.suppressSwiperIndexEmit = true;
    const panel = this.$refs.backdropContent;
    if (panel && panel.style) {
      panel.style.visibility = "hidden";
    }
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = "";
    }
  },

  methods: {
    getSwiper() {
      const c = this.$refs.detailSwiper;
      return c && c.$swiper ? c.$swiper : null;
    },
    close() {
      this.suppressSwiperIndexEmit = true;
      const panel = this.$refs.backdropContent;
      if (panel && typeof panel.style !== "undefined") {
        panel.style.visibility = "hidden";
      }
      this.$emit("update:visible", false);
    },
    onPrev() {
      if (this.canPrev) this.$emit("prev");
    },
    onNext() {
      if (this.canNext) this.$emit("next");
    },
    onSlideChangeTransitionEnd() {
      if (this.suppressSwiperIndexEmit) return;
      const inst = this.getSwiper();
      if (!inst) return;
      const i = inst.activeIndex;
      if (i !== this.activeIndex) {
        this.$emit("change-index", i);
      }
    },

    normalizeGoodsImages(item) {
      if (!item || item.img == null) return [];
      const raw = item.img;
      if (Array.isArray(raw)) return raw.filter(Boolean);
      return [raw].filter(Boolean);
    },
    galleryImageStatusKey(slideIdx, imgI, src) {
      return `${slideIdx}:${imgI}:${src || ""}`;
    },
    isGalleryImageLoading(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      const status = this.galleryImgLoadStatus[key];
      return !status || status === "loading";
    },
    onGalleryImageLoad(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      this.$set(this.galleryImgLoadStatus, key, "loaded");
    },
    onGalleryImageError(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      this.$set(this.galleryImgLoadStatus, key, "error");
    },

    /**
     * 商品标题多行：title 为 string[] 每项一行；或为 string（含换行则拆成多行）
     */
    normalizeGoodsTitleLines(item) {
      if (!item || item.title == null) return [];
      const raw = item.title;
      if (Array.isArray(raw)) {
        return raw.map((t) => String(t).trim()).filter(Boolean);
      }
      const s = String(raw).trim();
      if (!s) return [];
      return s
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    },

    goodsTitlePlain(item) {
      return this.normalizeGoodsTitleLines(item).join(" ");
    },

    normalizeGoodsInfoLines(item) {
      if (!item || item.info == null) return [];
      const raw = item.info;
      if (!Array.isArray(raw)) return [];
      return raw.map((t) => String(t).trim()).filter(Boolean);
    },

    clampedInnerImgIndex(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      if (!imgs.length) return 0;
      let stored = this.innerImgIndexBySlide[slideIdx];
      if (typeof stored !== "number" || stored < 0) stored = 0;
      return Math.min(stored, imgs.length - 1);
    },

    innerImgTrackStyle(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      const n = imgs.length;
      if (!n) {
        return { display: "none" };
      }
      const i = this.clampedInnerImgIndex(slideIdx);
      const ms = this.innerImgSlideMs;
      return {
        width: n * 100 + "%",
        transform: "translateX(-" + (i * 100) / n + "%)",
        transition:
          ms > 0
            ? "transform " + ms + "ms cubic-bezier(0.4, 0, 0.2, 1)"
            : "none",
      };
    },

    innerImgSlideCellStyle(slideItem) {
      const n = this.normalizeGoodsImages(slideItem).length;
      if (!n) return {};
      return { flex: "0 0 " + 100 / n + "%" };
    },

    goInnerImg(slideIdx, imageIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      if (imageIdx < 0 || imageIdx >= imgs.length) return;
      this.$set(this.innerImgIndexBySlide, slideIdx, imageIdx);
    },

    innerImgCanPrev(slideIdx) {
      return this.clampedInnerImgIndex(slideIdx) > 0;
    },

    innerImgCanNext(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      return (
        imgs.length > 1 && this.clampedInnerImgIndex(slideIdx) < imgs.length - 1
      );
    },

    onInnerImgPrev(slideIdx) {
      if (!this.innerImgCanPrev(slideIdx)) return;
      const next = this.clampedInnerImgIndex(slideIdx) - 1;
      this.$set(this.innerImgIndexBySlide, slideIdx, next);
    },

    onInnerImgNext(slideIdx) {
      if (!this.innerImgCanNext(slideIdx)) return;
      const next = this.clampedInnerImgIndex(slideIdx) + 1;
      this.$set(this.innerImgIndexBySlide, slideIdx, next);
    },
  },
};
</script>

<style lang="scss" scoped>
/* 弹层内图标按钮：重置 + 过渡；动效分「垂直居中 + scale」与「仅 scale」两种 */
@mixin pc-goods-detail-icon-btn-reset($size: 40px, $z-index: null) {
  margin: 0;
  padding: 0;
  width: $size;
  height: $size;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  appearance: none;
  transform-origin: center center;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
    filter 0.2s ease, opacity 0.2s ease;

  @if $z-index != null {
    z-index: $z-index;
  }

  img {
    display: block;
    width: $size;
    height: $size;
    object-fit: contain;
    object-position: center;
    pointer-events: none;
  }
}

@mixin pc-goods-detail-icon-btn-focus {
  &:focus-visible {
    outline: 2px solid #37fff1;
    outline-offset: 3px;
    border-radius: 4px;
  }
}

@mixin pc-goods-detail-icon-motion-y-center {
  transform: translateY(-50%);

  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.12);
    filter: brightness(1.14) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.9);
    filter: brightness(0.92);
    transition-duration: 0.08s;
  }
}

@mixin pc-goods-detail-icon-motion-scale-only {
  transform: none;

  &:hover:not(:disabled) {
    transform: scale(1.1);
    filter: brightness(1.12) drop-shadow(0 3px 10px rgba(0, 0, 0, 0.35));
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
    filter: brightness(0.9);
    transition-duration: 0.08s;
  }
}

@mixin pc-goods-detail-reduce-motion-y-center {
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &,
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: translateY(-50%);
      filter: none;
    }
  }
}

@mixin pc-goods-detail-reduce-motion-scale {
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &,
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: none;
      filter: none;
    }
  }
}

.pc-goods-detail {
  position: fixed;
  inset: 0;
  z-index: 200;

  .pc-goods-detail__bg {
    position: absolute;
    top: 71px;
    left: 0;
    z-index: 1;
    width: 1938px;
    height: 849px;
    object-fit: cover;
    object-position: center;
    pointer-events: none;
  }
}

/* 顶部 / 底部跑马灯共用骨架；皮肤用 --skin-* 区分背景条图与描边色 */
.pc-goods-detail__marquee {
  position: absolute;
  left: 0;
  width: 100%;
  height: 134px;
  z-index: 2;
  box-sizing: border-box;

  &--top {
    top: 0;
  }

  &--bottom {
    bottom: 0;
    padding-top: 1px;
  }

  &--skin-goods .pc-goods-detail__marquee-frame {
    background-image: url("@/assets/image/index/banner_footer_bg.webp");
  }

  &--skin-streaming .pc-goods-detail__marquee-frame {
    background-image: url("@/assets/image/index/about_footer_bg.webp");
  }

  &--skin-goods .pc-goods-detail__marquee-strip {
    border-top-color: #fff590;
    border-bottom-color: #fff590;
  }

  &--skin-streaming .pc-goods-detail__marquee-strip {
    border-top-color: #d9a2ff;
    border-bottom-color: #d9a2ff;
  }

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  .pc-goods-detail__marquee-frame {
    padding-top: 45px;
    position: relative;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  .pc-goods-detail__marquee-strip {
    overflow: hidden;
    height: 44px;
    display: flex;
    align-items: center;
    background-color: #191616;
    border-top: 1px solid;
    border-bottom: 1px solid;
  }

  .pc-goods-detail__marquee-track {
    display: flex;
    width: max-content;
    flex-shrink: 0;
    align-items: center;
    animation: pc-goods-detail-marquee-track 45s linear infinite;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  /* 须写在通用 .marquee-track 之后，否则 animation 简写会覆盖仅设的 animation-name */
  &--bottom .pc-goods-detail__marquee-track {
    animation: pc-goods-detail-marquee-track-reverse 45s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  .pc-goods-detail__marquee-chunk {
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  .pc-goods-detail__marquee-img {
    flex-shrink: 0;
    margin-right: 30px;
    width: 314px;
    height: 30px;
    object-fit: contain;
    object-position: center;
  }
}

.pc-goods-detail__backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  background-color: #3c3068;
  background-image: url("@/assets/image/detail/page_bg.webp");
  background-size: 100% auto;
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;

  /* 与商品列表页 .pc-goods__hero-bg 一致：超宽屏横向铺满 */
  .pc-goods-detail__backdrop-bg,
  .pc-goods__hero-bg {
    position: absolute;
    top: 0;
    left: 50%;
    width: 100vw;
    max-width: none;
    height: 1100px;
    transform: translateX(-50%);
    object-fit: cover;
    object-position: center;
    pointer-events: none;
  }
}

.pc-goods-detail__backdrop-content {
  --pc-goods-detail-design-width: 1447px;
  --pc-goods-detail-design-height: 720px;
  --pc-goods-detail-side-gap: 48px;
  --pc-goods-detail-top-gap: clamp(96px, 13vh, 150px);
  --pc-goods-detail-bottom-gap: clamp(96px, 14vh, 150px);
  --pc-goods-detail-scale-x: calc(
    (100vw - var(--pc-goods-detail-side-gap)) /
      var(--pc-goods-detail-design-width)
  );
  --pc-goods-detail-scale-y: calc(
    (
        100vh - var(--pc-goods-detail-top-gap) -
          var(--pc-goods-detail-bottom-gap)
      ) / var(--pc-goods-detail-design-height)
  );
  --pc-goods-detail-scale-base: min(
    var(--pc-goods-detail-scale-x),
    var(--pc-goods-detail-scale-y)
  );
  --pc-goods-detail-scale: clamp(0.2, var(--pc-goods-detail-scale-base), 1);
  position: absolute;
  top: var(--pc-goods-detail-top-gap);
  left: 50%;
  margin-left: calc(var(--pc-goods-detail-design-width) / -2);
  z-index: 3;
  transform: scale(var(--pc-goods-detail-scale));
  transform-origin: top center;
  width: var(--pc-goods-detail-design-width);
  height: var(--pc-goods-detail-design-height);
  box-sizing: border-box;
  pointer-events: auto;

  .pc-goods-detail__btn {
    position: absolute;
    top: 50%;
    @include pc-goods-detail-icon-btn-reset(40px, 3);
    @include pc-goods-detail-icon-btn-focus;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }

    &--prev,
    &--next {
      @include pc-goods-detail-icon-motion-y-center;
      @include pc-goods-detail-reduce-motion-y-center;
    }

    &--prev {
      left: clamp(-70px, -4vw, -28px);
    }

    &--next {
      right: clamp(-70px, -4vw, -28px);
    }
  }

  .pc-goods-detail__slide {
    height: 100%;

    .pc-goods-detail__panel {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 100%;
      min-height: 0;
      padding: 60px 95px;
      box-sizing: border-box;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 8px;
      color: #fff;

      .pc-goods-detail__close {
        position: absolute;
        top: 10px;
        right: 10px;
        @include pc-goods-detail-icon-btn-reset(40px, 5);
        @include pc-goods-detail-icon-btn-focus;
        @include pc-goods-detail-icon-motion-scale-only;
        @include pc-goods-detail-reduce-motion-scale;
      }

      .pc-goods-detail__gallery-stack {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 600px;
        flex-shrink: 0;
      }

      .pc-goods-detail__gallery {
        position: relative;
        padding: 20px;
        width: 100%;
        box-sizing: border-box;
        height: 600px;
        object-fit: contain;
        object-position: center;
        background-color: #191616;
        border-radius: 8px;
        border: 1px solid #fff380;

        &--multi .pc-goods-detail__gallery-viewport {
          height: 100%;
        }

        &-nav {
          position: absolute;
          top: 50%;
          @include pc-goods-detail-icon-btn-reset(30px, 2);
          @include pc-goods-detail-icon-btn-focus;
          @include pc-goods-detail-icon-motion-y-center;
          @include pc-goods-detail-reduce-motion-y-center;

          &--prev {
            left: 10px;
          }

          &--next {
            right: 10px;
          }

          &:disabled {
            cursor: not-allowed;
            opacity: 0.35;
          }
        }

        &-viewport {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        &-track {
          display: flex;
          height: 100%;
          min-height: 0;
          will-change: transform;
        }

        &-slide {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-width: 0;
          min-height: 0;
          box-sizing: border-box;
        }

        &-img-loading {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(
            180deg,
            rgba(25, 22, 22, 0.7) 0%,
            rgba(25, 22, 22, 0.42) 100%
          );
          z-index: 1;
          pointer-events: none;
        }

        &-img-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid rgba(255, 243, 128, 0.3);
          border-top-color: #fff380;
          border-radius: 50%;
          animation: pc-goods-detail-img-spin 0.9s linear infinite;
        }

        /* 铺满 slide，由 object-fit: contain 按父级宽高等比缩放，长边贴边、整图可见并居中 */
        &-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          transition: opacity 0.2s ease;

          &.is-loading {
            opacity: 0;
          }
        }

        .pc-goods-detail__gallery-dots {
          position: absolute;
          left: 50%;
          bottom: -27px;
          z-index: 4;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin: 0;
          padding: 0;
          max-width: calc(100% - 100px);
          box-sizing: border-box;
          transform: translateX(-50%);
          pointer-events: none;

          .pc-goods-detail__gallery-dot {
            pointer-events: auto;
          }
        }
      }

      .pc-goods-detail__gallery-dot {
        flex-shrink: 0;
        margin: 0;
        padding: 0;
        width: 8px;
        height: 8px;
        border: none;
        border-radius: 50%;
        background-color: #898989;
        cursor: pointer;
        appearance: none;
        transition: background-color 0.2s ease, transform 0.2s ease;

        &:hover {
          transform: scale(1.15);
        }

        &--active {
          background-color: #fff380;
        }

        &:focus-visible {
          outline: 2px solid #37fff1;
          outline-offset: 2px;
        }
      }

      .pc-goods-detail__info {
        flex: 1;
        min-width: 0;
        max-width: 100%;
        margin-left: 47px;
        text-align: left;

        &-desc-wrapper {
          height: 228px;
        }

        &-banner {
          margin: 0 0 30px 0;
          width: 600px;
          max-width: 100%;
          object-fit: contain;
          object-position: center;
        }

        &-title {
          margin: 20px 0 40px;
          padding: 0;
          color: #fff380;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.45;
          overflow-wrap: break-word;
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          &-line {
            display: block;
            max-width: 100%;

            &:not(:last-child) {
              margin-bottom: 10px;
            }
          }
        }

        &-desc {
          color: #fff;
          font-size: 16px;
          line-height: 1.5;
          font-weight: 600;
        }

        &-price {
          margin: 0 0 20px;
          font-size: 40px;
          font-weight: 700;
          text-align: left;
          color: #fff;

          &-label {
            margin-right: 30px;
            font-size: 20px;
            font-weight: 600;
          }
        }

        &-price-plain {
          display: block;
          width: 100%;
          margin: 0 0 20px;
          font-size: 28px;
          font-weight: 700;
          line-height: 1.4;
          text-align: left;
          color: #fff380;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        &-limit {
          margin: 20px 0 30px;
          width: 100%;
          line-height: 50px;
          font-size: 14px;
          font-weight: 600;
          color: #ffed9d;
          text-align: center;
          border: 1px solid #fff380;
          border-radius: 8px;
          cursor: default;

          span {
            color: #fff;
          }
        }
      }
    }
  }
}

.pc-goods-detail__swiper {
  width: 100%;
  height: 100%;
  &::v-deep .swiper-container {
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  &::v-deep .swiper-wrapper {
    align-items: center;
    height: 100%;
  }

  &::v-deep .swiper-slide {
    box-sizing: border-box;
    height: 100%;
  }
}

.pc-goods-detail__slide {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.pc-goods-detail-enter-active,
.pc-goods-detail-leave-active {
  transition: opacity 0.32s ease;
}

.pc-goods-detail-enter,
.pc-goods-detail-leave-to {
  opacity: 0;
}

@keyframes pc-goods-detail-img-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pc-goods-detail__gallery-dot {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}
</style>

<style lang="scss">
@keyframes pc-goods-detail-marquee-track {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* 底部跑马灯：与顶部相反，视觉向右滚动（无缝双段） */
@keyframes pc-goods-detail-marquee-track-reverse {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
